import { Popover } from 'antd';
import { css } from '@emotion/css';
import { X } from 'lucide-react';
import { useCallback, useState } from 'react';

interface PickerPopoverProps {
  title: string;
  overlayClassName?: string;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  headerExtra?: React.ReactNode;
  onVisibleChange?: (visible: boolean) => void;
}

interface ContentProps {
  title: string;
  children?: React.ReactNode;
  headerExtra?: React.ReactNode;
  onClose?: () => void;
}

const Content = (props: ContentProps) => {
  const { title, children, headerExtra, onClose } = props;
  return (
    <div className="flex flex-col min-w-[560px]">
      <header className="flex items-center justify-between px-4 py-3 border-0 border-b border-solid border-[#F0F2F5]">
        <div className="text-[#232426] font-medium">{title}</div>
        <div className="flex items-center justify-center text-[#84868C] gap-3">
          {headerExtra}
          <X className="cursor-pointer" size={16} onClick={onClose} />
        </div>
      </header>
      <div className="flex-1 overflow-y-auto">{children}</div>
    </div>
  );
};

export const PickerPopover = (props: PickerPopoverProps) => {
  const { icon, children, title, headerExtra, onVisibleChange } = props;
  const [visible, setVisible] = useState(false);

  const handleClose = useCallback(() => {
    setVisible(false);
    onVisibleChange?.(false);
  }, [onVisibleChange]);

  const handleInternalVisibleChange = useCallback(
    (visible: boolean) => {
      setVisible(visible);
      onVisibleChange?.(visible);
    },
    [onVisibleChange],
  );

  return (
    <Popover
      visible={visible}
      onVisibleChange={handleInternalVisibleChange}
      content={
        <Content title={title} headerExtra={headerExtra} onClose={handleClose}>
          {children}
        </Content>
      }
      trigger="click"
      overlayClassName={innerContentClass}
    >
      {icon}
    </Popover>
  );
};

const innerContentClass = css`
  .ant-popover-inner-content {
    padding: 0;
    border-radius: 4px;
  }

  .ant-popover-arrow {
    display: none;
  }
`;
